import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { MapPin, Search, CheckCircle, Loader2, ChevronLeft, ChevronRight, ChevronDown, X, Filter } from 'lucide-react';
import { publicPartnerAPI } from '../services/api';
import TherapistCard from '../components/therapists/TherapistCard';
import Footer from '../components/layout/Footer';

// Filter option constants
const EXPERTISE_OPTIONS = [
  'Anxiety',
  'Depression',
  'Stress Management',
  'Relationship Issues',
  'Trauma & PTSD',
  'Grief & Loss',
  'Self-Esteem',
  'Anger Management',
  'OCD',
  'Phobias',
  'Bipolar Disorder',
  'ADHD',
  'Autism Spectrum',
  'Eating Disorders',
  'Addiction & Substance Abuse',
  'Sleep Disorders',
  'Chronic Pain',
  'Sexual Health',
  'Gender Identity',
  'LGBTQ+ Issues',
  'Parenting',
  'Career Counseling',
  'Academic Stress',
  'Workplace Issues',
  'Domestic Violence',
  'Child Behavior',
  'Adolescent Issues',
  'Personality Disorders',
  'Psychosomatic Disorders',
  'Mindfulness & Meditation',
  'Life Transitions'
];

const THERAPY_CATEGORY_OPTIONS = [
  'Couples Therapy',
  'Family Therapy',
  'Child Therapy'
];

const ALTERNATIVE_THERAPY_OPTIONS = [
  'Hypnotherapy',
  'Reiki',
  'Yoga Therapy',
  'Aroma Therapy',
  'Chiropractic Care',
  'Breathwork',
  'Reflexology',
  'Craniosacral Therapy',
  'Energy Healing'
];

const LANGUAGE_OPTIONS = [
  'English',
  'Hindi',
  'Bengali',
  'Telugu',
  'Marathi',
  'Tamil',
  'Urdu',
  'Gujarati',
  'Kannada',
  'Malayalam',
  'Punjabi',
  'Odia',
  'Assamese',
  'Sanskrit',
  'Other'
];

const PRICE_RANGES = [
  { label: 'Under ₹500', value: '0-500' },
  { label: '₹500 - ₹1,000', value: '500-1000' },
  { label: '₹1,000 - ₹1,500', value: '1000-1500' },
  { label: '₹1,500 - ₹2,000', value: '1500-2000' },
  { label: '₹2,000 - ₹2,500', value: '2000-2500' },
  { label: '₹2,500 - ₹3,000', value: '2500-3000' },
  { label: '₹3,000+', value: '3000-' }
];

const SEX_OPTIONS = ['Male', 'Female', 'Other'];

const Therapists = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get therapy type from navigation state or default to Individual Therapy
  const therapyTypeFromState = location.state?.therapyType || 'Individual Therapy';

  const [searchParams, setSearchParams] = useState({
    therapyType: therapyTypeFromState,
    sessionMode: 'Online',
    location: '',
    keywords: '',
    expertise: [],
    languages: [],
    sex: '',
    priceRange: ''
  });

  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, totalPages: 0 });
  const [mcid, setMcid] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [expertiseSearch, setExpertiseSearch] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [collapsedFilters, setCollapsedFilters] = useState({
    expertise: true,
    priceRange: true,
    languages: true,
    sex: true
  });

  const therapyTypes = [
    'Individual Therapy',
    'Couples Therapy',
    'Child Therapy',
    'Alternative Therapies'
  ];

  const sessionModes = ['Online', 'In-Person', 'Both'];

  const trustFeatures = [
    { icon: CheckCircle, text: 'Verified & Qualified Therapists' },
    { icon: CheckCircle, text: 'Secure & Private Platform' },
    { icon: CheckCircle, text: 'Ethical & Boundary-Safe' },
    { icon: CheckCircle, text: 'Confidential & Discreet' }
  ];

  const fetchTherapists = useCallback(async (params = {}, page = 1) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const queryParams = { page, limit: 12 };
      if (params.therapyType) queryParams.therapyType = params.therapyType;
      if (params.sessionMode && params.sessionMode !== 'Both') queryParams.sessionMode = params.sessionMode;
      if (params.location) queryParams.location = params.location;
      if (params.keywords) queryParams.keywords = params.keywords;

      // New filter params
      if (params.expertise && params.expertise.length > 0) {
        queryParams.expertise = params.expertise.join(',');
      }
      if (params.languages && params.languages.length > 0) {
        queryParams.languages = params.languages.join(',');
      }
      if (params.sex) {
        queryParams.sex = params.sex;
      }
      if (params.priceRange) {
        const parts = params.priceRange.split('-');
        if (parts[0]) queryParams.priceMin = parts[0];
        if (parts[1]) queryParams.priceMax = parts[1];
      }

      const response = await publicPartnerAPI.searchTherapists(queryParams);
      setTherapists(response.data.therapists);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to fetch therapists:', err);
      setError('Failed to load therapists. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch marketplace tracking ID on mount
  useEffect(() => {
    publicPartnerAPI.getMarketplaceTrackingId()
      .then(res => setMcid(res.data.mcid))
      .catch(err => console.error('Failed to fetch marketplace tracking ID:', err));
  }, []);

  // Load on mount, auto-search if therapy type came from Home page
  useEffect(() => {
    if (location.state?.therapyType) {
      fetchTherapists({ therapyType: location.state.therapyType });
    }
    // Don't fetch on initial load - only after user performs a search
  }, [location.state?.therapyType, fetchTherapists]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTherapists(searchParams, 1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchTherapists(searchParams, newPage);
    // Scroll to results
    document.getElementById('search-results')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field, value) => {
    setSearchParams(prev => {
      const current = prev[field];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [field]: updated };
    });
  };

  const clearFilters = () => {
    setSearchParams(prev => ({
      ...prev,
      expertise: [],
      languages: [],
      sex: '',
      priceRange: ''
    }));
    setExpertiseSearch('');
  };

  const hasActiveFilters = searchParams.expertise.length > 0 || searchParams.languages.length > 0 || searchParams.sex !== '' || searchParams.priceRange !== '';

  const filteredExpertiseOptions = EXPERTISE_OPTIONS.filter(opt =>
    opt.toLowerCase().includes(expertiseSearch.toLowerCase())
  );

  const toggleFilterSection = (section) => {
    setCollapsedFilters(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Filter sidebar content (shared between desktop and mobile)
  // Using a JSX variable instead of a component function to avoid re-mounting on state changes
  const filterContent = (
    <div className="space-y-3">
      {/* Clear Filters */}
      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition"
        >
          <X className="h-4 w-4" />
          Clear All Filters
        </button>
      )}

      {/* Expertise Section */}
      <div className="border-b border-gray-200 dark:border-dark-border pb-3">
        <button
          type="button"
          onClick={() => toggleFilterSection('expertise')}
          className="w-full flex items-center justify-between py-1 text-sm font-semibold text-gray-900 dark:text-dark-text-primary"
        >
          <span>Expertise {searchParams.expertise.length > 0 && `(${searchParams.expertise.length})`}</span>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${collapsedFilters.expertise ? '-rotate-90' : ''}`} />
        </button>
        {!collapsedFilters.expertise && (
          <div className="mt-2">
            <input
              type="text"
              value={expertiseSearch}
              onChange={(e) => setExpertiseSearch(e.target.value)}
              placeholder="Search expertise..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-tertiary mb-2"
            />
            <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
              {filteredExpertiseOptions.map(option => (
                <label key={option} className="flex items-center gap-2 cursor-pointer py-0.5">
                  <input
                    type="checkbox"
                    checked={searchParams.expertise.includes(option)}
                    onChange={() => handleCheckboxChange('expertise', option)}
                    className="h-4 w-4 rounded border-gray-300 dark:border-dark-border text-primary-600 dark:text-dark-primary-600 focus:ring-primary-500 dark:focus:ring-dark-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-dark-text-secondary">{option}</span>
                </label>
              ))}

              {/* Therapy Categories subsection */}
              {THERAPY_CATEGORY_OPTIONS.some(o => o.toLowerCase().includes(expertiseSearch.toLowerCase())) && (
                <>
                  <p className="text-xs font-semibold text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wide mt-3 mb-1">Therapy Categories</p>
                  {THERAPY_CATEGORY_OPTIONS.filter(o => o.toLowerCase().includes(expertiseSearch.toLowerCase())).map(option => (
                    <label key={option} className="flex items-center gap-2 cursor-pointer py-0.5">
                      <input
                        type="checkbox"
                        checked={searchParams.expertise.includes(option)}
                        onChange={() => handleCheckboxChange('expertise', option)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-dark-border text-primary-600 dark:text-dark-primary-600 focus:ring-primary-500 dark:focus:ring-dark-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-dark-text-secondary">{option}</span>
                    </label>
                  ))}
                </>
              )}

              {/* Alternative Therapies subsection */}
              {ALTERNATIVE_THERAPY_OPTIONS.some(o => o.toLowerCase().includes(expertiseSearch.toLowerCase())) && (
                <>
                  <p className="text-xs font-semibold text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wide mt-3 mb-1">Alternative Therapies</p>
                  {ALTERNATIVE_THERAPY_OPTIONS.filter(o => o.toLowerCase().includes(expertiseSearch.toLowerCase())).map(option => (
                    <label key={option} className="flex items-center gap-2 cursor-pointer py-0.5">
                      <input
                        type="checkbox"
                        checked={searchParams.expertise.includes(option)}
                        onChange={() => handleCheckboxChange('expertise', option)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-dark-border text-primary-600 dark:text-dark-primary-600 focus:ring-primary-500 dark:focus:ring-dark-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-dark-text-secondary">{option}</span>
                    </label>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Price Range Section */}
      <div className="border-b border-gray-200 dark:border-dark-border pb-3">
        <button
          type="button"
          onClick={() => toggleFilterSection('priceRange')}
          className="w-full flex items-center justify-between py-1 text-sm font-semibold text-gray-900 dark:text-dark-text-primary"
        >
          <span>Price Range {searchParams.priceRange && '(1)'}</span>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${collapsedFilters.priceRange ? '-rotate-90' : ''}`} />
        </button>
        {!collapsedFilters.priceRange && (
          <div className="mt-2 space-y-1">
            {PRICE_RANGES.map(range => (
              <label key={range.value} className="flex items-center gap-2 cursor-pointer py-0.5">
                <input
                  type="radio"
                  name="priceRange"
                  value={range.value}
                  checked={searchParams.priceRange === range.value}
                  onChange={() => handleInputChange('priceRange', range.value)}
                  className="h-4 w-4 border-gray-300 dark:border-dark-border text-primary-600 dark:text-dark-primary-600 focus:ring-primary-500 dark:focus:ring-dark-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">{range.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Languages Section */}
      <div className="border-b border-gray-200 dark:border-dark-border pb-3">
        <button
          type="button"
          onClick={() => toggleFilterSection('languages')}
          className="w-full flex items-center justify-between py-1 text-sm font-semibold text-gray-900 dark:text-dark-text-primary"
        >
          <span>Languages {searchParams.languages.length > 0 && `(${searchParams.languages.length})`}</span>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${collapsedFilters.languages ? '-rotate-90' : ''}`} />
        </button>
        {!collapsedFilters.languages && (
          <div className="mt-2 max-h-48 overflow-y-auto space-y-1 pr-1">
            {LANGUAGE_OPTIONS.map(lang => (
              <label key={lang} className="flex items-center gap-2 cursor-pointer py-0.5">
                <input
                  type="checkbox"
                  checked={searchParams.languages.includes(lang)}
                  onChange={() => handleCheckboxChange('languages', lang)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-dark-border text-primary-600 dark:text-dark-primary-600 focus:ring-primary-500 dark:focus:ring-dark-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">{lang}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Sex Section */}
      <div>
        <button
          type="button"
          onClick={() => toggleFilterSection('sex')}
          className="w-full flex items-center justify-between py-1 text-sm font-semibold text-gray-900 dark:text-dark-text-primary"
        >
          <span>Therapist Gender {searchParams.sex && '(1)'}</span>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${collapsedFilters.sex ? '-rotate-90' : ''}`} />
        </button>
        {!collapsedFilters.sex && (
          <div className="mt-2 space-y-1">
            {SEX_OPTIONS.map(option => (
              <label key={option} className="flex items-center gap-2 cursor-pointer py-0.5">
                <input
                  type="radio"
                  name="sex"
                  value={option}
                  checked={searchParams.sex === option}
                  onChange={() => handleInputChange('sex', option)}
                  className="h-4 w-4 border-gray-300 dark:border-dark-border text-primary-600 dark:text-dark-primary-600 focus:ring-primary-500 dark:focus:ring-dark-primary-500"
                />
                <span className="text-sm text-gray-700 dark:text-dark-text-secondary">{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section with Background */}
      <div className="relative overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/homePage1.png')`,
            opacity: 0.95
          }}
        ></div>

        {/* Overlay for better readability */}
        <div className="absolute inset-0 bg-white/30 dark:bg-dark-bg-primary/30"></div>

        {/* Content */}
        <div className="relative z-10">
          {/* Header/Navigation */}
          <nav className="bg-white/80 dark:bg-dark-bg-primary/80 backdrop-blur-md shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-20">
                <Link to="/" className="flex items-center space-x-3">
                  <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center p-2 shadow-md border border-yellow-500">
                    <img
                      src="/TheraPTrackLogoBgRemoved.png"
                      alt="TheraP Track Logo"
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <span className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">
                    TheraP Track
                  </span>
                </Link>

                <Link
                  to="/login"
                  className="px-6 py-2 bg-primary-600 dark:bg-dark-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition"
                >
                  Login
                </Link>
              </div>
            </div>
          </nav>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Header Text - Full Width */}
            <div className="space-y-3 mb-8 lg:pl-[calc(33.333%+2rem)]">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-dark-text-primary">
                TheraP Track
              </h1>
              <p className="text-lg text-gray-700 dark:text-dark-text-secondary leading-relaxed">
                connects you with over 500+ verified therapists ready to support your mental health.
                From individual sessions to family therapy, explore the help you need.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 items-start">
              {/* Left Side - Filter Sidebar */}
              <div className="hidden lg:block">
                <div className="bg-white/90 dark:bg-dark-bg-tertiary/90 backdrop-blur-sm rounded-2xl p-5 shadow-xl sticky top-4">
                  <h2 className="text-base font-bold text-gray-900 dark:text-dark-text-primary mb-4 flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Filters
                  </h2>
                  {filterContent}
                </div>
              </div>

              {/* Mobile Filter Toggle */}
              <div className="lg:hidden mb-4">
                <button
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/90 dark:bg-dark-bg-tertiary/90 backdrop-blur-sm rounded-lg shadow-md text-sm font-medium text-gray-700 dark:text-dark-text-secondary"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {hasActiveFilters && (
                    <span className="bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {searchParams.expertise.length + searchParams.languages.length + (searchParams.sex ? 1 : 0) + (searchParams.priceRange ? 1 : 0)}
                    </span>
                  )}
                </button>
                {showMobileFilters && (
                  <div className="mt-3 bg-white/90 dark:bg-dark-bg-tertiary/90 backdrop-blur-sm rounded-2xl p-5 shadow-xl">
                    {filterContent}
                  </div>
                )}
              </div>

              {/* Right Side - Search Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* Search Form */}
                <form onSubmit={handleSearch} className="space-y-4 bg-white/90 dark:bg-dark-bg-tertiary/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                  {/* Therapy Type and Session Mode Row */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Therapy Type Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                        I'm looking for
                      </label>
                      <select
                        value={searchParams.therapyType}
                        onChange={(e) => handleInputChange('therapyType', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-dark-primary-500 focus:border-primary-500 dark:focus:border-dark-primary-500 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary"
                      >
                        {therapyTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Session Mode Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                        Session Mode
                      </label>
                      <select
                        value={searchParams.sessionMode}
                        onChange={(e) => handleInputChange('sessionMode', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-dark-primary-500 focus:border-primary-500 dark:focus:border-dark-primary-500 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary"
                      >
                        {sessionModes.map((mode) => (
                          <option key={mode} value={mode}>
                            {mode}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Location Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                      Enter your location
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={searchParams.location}
                        onChange={(e) => handleInputChange('location', e.target.value)}
                        placeholder="Enter your location"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-dark-primary-500 focus:border-primary-500 dark:focus:border-dark-primary-500 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-tertiary"
                      />
                    </div>
                  </div>

                  {/* Keywords Search Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                      Search for therapist or specialty
                    </label>
                    <input
                      type="text"
                      value={searchParams.keywords}
                      onChange={(e) => handleInputChange('keywords', e.target.value)}
                      placeholder="Search for Counseling Psychologist, Clinical Psychologist, Hypnotherapist, Child Psychologist, Physiotherapist and others..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-dark-primary-500 focus:border-primary-500 dark:focus:border-dark-primary-500 bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-tertiary"
                    />
                  </div>

                  {/* Search Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full md:w-auto px-8 py-3 bg-primary-600 dark:bg-dark-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Search className="h-5 w-5" />
                    )}
                    Search
                  </button>
                </form>

                {/* Trust Features */}
                <div className="grid md:grid-cols-2 gap-4">
                  {trustFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <feature.icon className="h-5 w-5 text-green-600 dark:text-green-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary">
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Results Section */}
      <div id="search-results" className="bg-gray-50 dark:bg-dark-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          {/* Initial State - No Search Performed */}
          {!hasSearched && !loading && (
            <div className="text-center py-16">
              <Search className="h-16 w-16 text-gray-300 dark:text-dark-text-tertiary mx-auto mb-4" />
              <p className="text-xl font-medium text-gray-600 dark:text-dark-text-secondary">
                Start Your Search
              </p>
              <p className="text-sm text-gray-500 dark:text-dark-text-tertiary mt-2">
                Use the search form above to find the right therapist for you
              </p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-primary-600 dark:text-dark-primary-500" />
            </div>
          )}

          {/* Error State */}
          {!loading && error && hasSearched && (
            <div className="text-center py-16">
              <p className="text-red-600 dark:text-red-400 text-lg">{error}</p>
              <button
                onClick={() => fetchTherapists(searchParams, pagination.page)}
                className="mt-4 px-6 py-2 bg-primary-600 dark:bg-dark-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 dark:hover:bg-dark-primary-700 transition"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && therapists.length === 0 && hasSearched && (
            <div className="text-center py-16">
              <Search className="h-12 w-12 text-gray-400 dark:text-dark-text-tertiary mx-auto mb-4" />
              <p className="text-lg text-gray-600 dark:text-dark-text-secondary">
                No therapists found matching your criteria.
              </p>
              <p className="text-sm text-gray-500 dark:text-dark-text-tertiary mt-2">
                Try adjusting your search filters or keywords.
              </p>
            </div>
          )}

          {/* Results List */}
          {!loading && !error && therapists.length > 0 && hasSearched && (
            <>
              <div className="space-y-4">
                {therapists.map((therapist) => (
                  <TherapistCard key={therapist.partner_id} therapist={therapist} mcid={mcid} listView={true} />
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-10">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-primary disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>

                  <span className="text-sm text-gray-600 dark:text-dark-text-secondary">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-bg-tertiary border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-primary disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Therapists;
